import FuseUtils from '@/@fuse/utils';
import AppContext from '@/app/AppContext';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { matchRoutes, useLocation, useNavigate } from 'react-router-dom';

const FuseAuthorization = ({ children }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const userRole = useSelector(({ auth }) => auth.user.role);
	const userPermission = useSelector(({ auth }) => auth.user.permissions);
	// Per-school billing feature flag. Undefined while the profile is still
	// loading — only an explicit `false` blocks, so we don't bounce users
	// mid-load (the effect re-runs once the flag resolves). TOP-LEVEL school
	// first: it's the masqueraded school (dialog + Auth.jsx boot refresh);
	// data.school is the entry-time snapshot and must not shadow it.
	const billingEnabled = useSelector(
		({ auth }) => (auth?.user?.school ?? auth?.user?.data?.school)?.billing_enabled,
	);
	const routes = useContext(AppContext).routes;
	const [accessGranted, setAccessGranted] = useState(true);

	const redirectRoute = () => {
		const { pathname } = location;

		// Don't redirect if accessing incident report routes
		if (pathname.startsWith('/teacher/incident-report/')) {
			return;
		}

		if (!userRole || userRole.length === 0) {
			navigate('/login', { state: { redirectUrl: pathname } });
		} else {
			if (['school_admin', 'sub_admin', 'super_school_admin'].includes(userRole[0])) {
				navigate('/home');
			}
			if (['super_admin', 'company_owner'].includes(userRole[0])) {
				navigate('/company');
			}
		}
	};

	useEffect(() => {
		const { pathname } = location;

		// Allow access to incident report routes without authentication
		if (pathname.startsWith('/teacher/incident-report')) {
			setAccessGranted(true);
			return;
		}

		// Per-school billing feature gate + per-user Billing permission —
		// block every /billing route when the flag is explicitly disabled
		// for the selected school, or when a loaded permissions array lacks
		// Billing (undefined-while-loading never blocks; the effect re-runs).
		const hasBillingPermission = userPermission
			?.map((permission) => permission?.replace(/^\/+/, ''))
			?.includes('Billing');
		if (
			pathname.startsWith('/billing') &&
			(billingEnabled === false || (userPermission != null && !hasBillingPermission))
		) {
			setAccessGranted(false);
			redirectRoute();
			return;
		}

		const perm = [
			'/students',
			'/staff',
			'/rooms',
			'/calendar',
			'/staff-schedule',
			'/messaging',
			'/livestreaming',
			'/reports',
			'/subadmin',
		];

		const filterUser = userPermission?.map((data) => data?.split('_').join('').toLowerCase());
		const filterPerm = userRole[0] === 'sub_admin' ? perm?.filter((data) => !filterUser?.includes(data)) : [];
		const paths = routes.filter((data) => !filterPerm?.includes(data.path?.split('-')[0]));
		const matched = matchRoutes(paths, location.pathname)?.[0];

		if (!matched || matched.route.path === '*') {
			setAccessGranted(false);
			redirectRoute();
		} else {
			const newAccessGranted = FuseUtils.hasPermission(matched.route.auth, userRole);
			setAccessGranted(newAccessGranted);
			if (!newAccessGranted) {
				redirectRoute();
			}
		}
	}, [location, userRole, userPermission, routes, billingEnabled]);

	return accessGranted ? children : null;
};

export default FuseAuthorization;
